import React, { createContext, useState, useEffect, useContext } from 'react';
import { msalInstance, initializeMsal } from '../services/msalInstance';
import { loginRequest, updateAuthority } from '../utils/constants/authConfig';
import { AuthContextType } from 'interfaces/auth';
import { AccountInfo } from '@azure/msal-browser';
import {
  getCompanyName,
  getUserClaims,
  getUsername,
  isUserApproved,
  isUserPortCo
} from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const handleApiError = (error: unknown): string => {
    if (error instanceof Error) {
      return error.message;
    }
    return 'An unexpected error occurred';
  };

  const ensureMsalInitialized = async (): Promise<void> => {
    try {
      await initializeMsal();
    } catch (error) {
      console.error('Failed to initialize MSAL:', error);
      throw error;
    }
  };

  const handleRedirect = async (): Promise<AccountInfo | null> => {
    try {
      setIsLoading(true);
      setError(null);

      await ensureMsalInitialized();

      const result = await msalInstance.handleRedirectPromise();

      if (result) {
        msalInstance.setActiveAccount(result.account);
        setUser(result.account);
        return result.account;
      } else {
        const currentAccount = msalInstance.getAllAccounts()[0];
        if (currentAccount) {
          msalInstance.setActiveAccount(currentAccount);
          setUser(currentAccount);
          return currentAccount;
        }
      }

      return null;
    } catch (err) {
      const errorMessage = handleApiError(err);
      console.error('Error handling redirect:', errorMessage);
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const initialize = async (): Promise<void> => {
    try {
      await handleRedirect();
    } catch (error) {
      console.warn('Auth initialization warning:', error);
    }
  };

  const getToken = async (): Promise<string> => {
    try {
      await ensureMsalInitialized();

      const account = msalInstance.getActiveAccount();
      if (!account) {
        throw new Error('No active account');
      }

      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account
      });

      return tokenResponse.accessToken;
    } catch (error) {
      try {
        const tokenResponse = await msalInstance.acquireTokenPopup(
          loginRequest
        );
        return tokenResponse.accessToken;
      } catch (popupError) {
        console.error('Token acquisition error:', popupError);
        throw new Error('Failed to acquire token');
      }
    }
  };

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    const initAuth = async () => {
      try {
        await initialize();
        setIsInitialized(true);
      } catch (error) {
        console.warn('Authentication initialization warning:', error);
        setIsInitialized(true);
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await ensureMsalInitialized();

      updateAuthority('signUpSignIn');
      await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true);

      await ensureMsalInitialized();

      const account = msalInstance.getActiveAccount();
      if (account) {
        await msalInstance.logoutRedirect({ account });
      }
      setUser(null);
      setError(null);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const userClaims = getUserClaims();
  const isApproved = isUserApproved();
  const companyName = getCompanyName();
  const isPortCo = isUserPortCo();
  const username = getUsername();

  const value: AuthContextType = {
    user,
    username,
    isLoading,
    isInitialized,
    error,
    signIn,
    signOut,
    getToken,
    clearError,
    initialize,
    userClaims,
    isApproved,
    isAuthenticated: !!user,
    companyName,
    isPortCo
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthContext, AuthProvider, useAuth };
